/**
 * Data router loaders for auth bootstrap and route protection.
 * SECURITY: Loaders run before route render; auth checks prevent unauthorized access.
 * ASSUMPTION: DefaultStore is singleton; bootstrap runs once per session.
 */

import { redirect } from "react-router";
import {
  ACCOUNT_ROLE_ISSUE_SESSION_KEY,
  AUTH_ROLES,
  ROLE_DEFAULT_PATHS,
  userIdSuffixForOperationalLog,
} from "@/constants/auth";
import {
  awaitAuthBootstrap as awaitSessionBootstrap,
  resetBootstrapForTesting as resetAuthBootstrapForTesting,
  scheduleBootstrapInBackground,
  refreshAuthSession,
  isBootstrapComplete,
} from "@/auth/authBootstrap";
import { scheduleNonCriticalWorkOnce } from "@/utils/scheduleNonCriticalWorkOnce";
import { getSafeInternalRedirectPath } from "@/utils/safeInternalRedirect";
import { IS_DEV } from "@/utils/env";
import DefaultStore from "@/store/Store";
import {
  getLastSessionRevalidatedAt,
  invalidateSessionLoaderState,
  isDeferredAuthHydrationQueued,
  setDeferredAuthHydrationQueued,
  setLastSessionRevalidatedAt,
} from "./sessionLoaderState";

/** Cognito group strings that map to app concepts; reused when diagnosing missing `AuthStore.role`. */
const KNOWN_COGNITO_GROUP_NAMES = new Set(Object.values(AUTH_ROLES));
export { awaitAuthBootstrap } from "@/auth/authBootstrap";

/** Min interval between throttled session refreshes in `protectedLayoutLoader` (after initial bootstrap). */
const SESSION_REFRESH_TTL_MS = 1 * 60 * 60 * 1000;

/** Public shared view routes — auth is optional; session hydration must not block first paint. */
function isNonBlockingPublicViewRoute(pathname) {
  const path = String(pathname ?? "").replace(/\/+$/, "");
  return ["/deallens/view", "/property/photos/view"].includes(path);
}

/**
 * @param {Request} request
 * @returns {string} Location for `redirect()` — sign-in with safe return path (path + search only).
 */
function signinLocationPreservingReturnPath(request) {
  const url = new URL(request.url);
  const pathAndSearch = `${url.pathname}${url.search}`;
  const safeRedirect = getSafeInternalRedirectPath(pathAndSearch, url.origin) ?? "/";
  return `/signin?redirect=${encodeURIComponent(safeRedirect)}`;
}

/**
 * Cognito session exists but `AuthStore.role` is empty (no recognized app group). Clears session and redirects to sign-in.
 * @returns {Promise<never>}
 */
async function handleMissingAppRole() {
  const authStore = DefaultStore.authStore;
  const rawGroups = Array.isArray(authStore.groups) ? authStore.groups.map((g) => String(g)) : [];
  const unmatched = rawGroups.filter((g) => !KNOWN_COGNITO_GROUP_NAMES.has(g));
  const suffix = userIdSuffixForOperationalLog(authStore.userId);
  const endsWithPhrase = `User ID that ends with "${suffix}".`;

  let detail;
  if (rawGroups.length === 0) {
    detail = `No Cognito groups on the token; application role is empty. ${endsWithPhrase}`;
  } else if (unmatched.length > 0) {
    const unmatchedQuoted = unmatched.map((g) => `"${g}"`).join(", ");
    detail = `Unmatched Cognito group(s): ${unmatchedQuoted}. ${endsWithPhrase}`;
  } else {
    const groupsQuoted = rawGroups.map((g) => `"${g}"`).join(", ");
    detail = `Cognito group(s) ${groupsQuoted} match known names but application role is empty — check AuthStore role mapping. ${endsWithPhrase}`;
  }

  console.warn(`[horAIzen] Missing application role. Signing out. ${detail}`);
  try {
    sessionStorage.setItem(ACCOUNT_ROLE_ISSUE_SESSION_KEY, "1");
  } catch {
    // ignore (private mode / quota)
  }
  await DefaultStore.signOut();
  throw redirect("/signin");
}

function hasRecognizedAppRole(authStore) {
  return Boolean(String(authStore?.role ?? "").trim());
}

/**
 * Re-validates Cognito session when TTL elapsed so `userId` is not trusted indefinitely after bootstrap.
 * @returns {Promise<void>}
 */
async function ensureSessionFreshThrottled() {
  const { authStore } = DefaultStore;
  const now = Date.now();
  if (now - getLastSessionRevalidatedAt() < SESSION_REFRESH_TTL_MS) return;
  setLastSessionRevalidatedAt(Date.now());
  await authStore.getGlobalInformation({ forceRefresh: true, fetchProfile: true });
}

/** Vitest: backdate session clock so the next `ensureSessionFreshThrottled` runs. */
export function setSessionRevalidatedAtForTesting(ts) {
  setLastSessionRevalidatedAt(ts);
}

export { invalidateSessionLoaderState };

/** Vitest: reset bootstrap flags so loader tests can re-run bootstrap. */
export function resetBootstrapForTesting() {
  resetAuthBootstrapForTesting();
  invalidateSessionLoaderState();
}

/** Vitest: TTL for throttle assertions. */
export function getSessionRefreshTtlMsForTesting() {
  return SESSION_REFRESH_TTL_MS;
}

/**
 * Queue profile, accounts, files, and agents off the loader critical path (after paint / idle / input).
 * SECURITY: Same auth-scoped queries as before; only timing changes. Skips when unauthenticated.
 */
function scheduleDeferredAuthSideEffects() {
  const { authStore } = DefaultStore;
  if (!authStore.userId || isDeferredAuthHydrationQueued()) return;
  setDeferredAuthHydrationQueued(true);
  scheduleNonCriticalWorkOnce(async () => {
    const a = DefaultStore.authStore;
    if (!a.userId) return;
    await Promise.all([a.fetchUserProfile(), a.fetchAccounts()]);
    if (!a.userId) return;
    await Promise.all([a.fetchFiles(a.userId), a.fetchAgents(a.userId)]);
  });
}

/**
 * Populate AuthStore (Cognito, accounts; files/agents deferred). Idempotent per session.
 * SECURITY: Single bootstrap prevents race conditions; module flag ensures one run.
 * Property list cache warming runs from AuthenticatedLayoutWrapper after first paint.
 */
async function bootstrap() {
  const wasBootstrapped = isBootstrapComplete();
  await awaitSessionBootstrap();
  if (!wasBootstrapped) {
    setLastSessionRevalidatedAt(Date.now());
  }
  if (DefaultStore.authStore.userId) {
    scheduleDeferredAuthSideEffects();
  }
}

/**
 * Post-sign-in hydration: refresh Cognito session + profile, then defer accounts/files/agents
 * (same critical path as cold `bootstrap()` so SignIn is not blocked on four serial GraphQL calls).
 */
export async function runBootstrap() {
  const start = Date.now();

  try {
    await refreshAuthSession();
    setLastSessionRevalidatedAt(Date.now());
    if (DefaultStore.authStore.userId) {
      setDeferredAuthHydrationQueued(false);
      scheduleDeferredAuthSideEffects();
    }

    if (IS_DEV) {
      console.log("[runBootstrap] Completed", {
        totalMs: Date.now() - start,
      });
    }
  } catch (err) {
    if (IS_DEV) {
      console.error("[runBootstrap] Failed after", Date.now() - start, "ms", {
        error: err?.message,
      });
    }
    throw err;
  }
}

/**
 * Root loader: run bootstrap before any route. Parent runs first so auth is ready for children.
 */
/**
 * Session hydration before React Router is created (Option A — loaders see hydrated AuthStore).
 * Call once from `index.jsx` after `Amplify.configure` (before any `graphqlRequest`).
 */
export async function ensureSessionBeforeRouter() {
  if (typeof window !== "undefined" && isNonBlockingPublicViewRoute(window.location.pathname)) {
    scheduleBootstrapInBackground();
    return;
  }
  await bootstrap();
}

/**
 * @param {{ request: Request }} args
 */
export async function rootLoader({ request }) {
  if (request?.url && isNonBlockingPublicViewRoute(new URL(request.url).pathname)) {
    scheduleBootstrapInBackground();
    return {};
  }
  await bootstrap();
  return {};
}

const FALLBACK_DEFAULT_ROUTE = "/soon";

/**
 * Shared default route selection by role (see `ROLE_DEFAULT_PATHS` in `@/constants/auth`).
 */
export function getDefaultRoute(authStore) {
  if (!authStore) return FALLBACK_DEFAULT_ROUTE;
  const role = String(authStore.role ?? "")
    .trim()
    .toLowerCase();
  const mapped = role ? ROLE_DEFAULT_PATHS[role] : "";
  return mapped || FALLBACK_DEFAULT_ROUTE;
}

/**
 * Index route (/): redirect unauthenticated to signin, authenticated to role default (see `ROLE_DEFAULT_PATHS` in `@/constants/auth`).
 */
export async function indexLoader() {
  await bootstrap();
  const a = DefaultStore.authStore;
  if (!a.userId) throw redirect("/signin");
  if (!hasRecognizedAppRole(a)) {
    await handleMissingAppRole();
  }
  throw redirect(getDefaultRoute(a));
}

/**
 * Protected layout loader: redirect to signin if not authenticated; preserve path for post-login return.
 * Re-validates session on a TTL after bootstrap so expired Cognito sessions clear `userId` before child routes render.
 */
export async function protectedLayoutLoader({ request }) {
  await bootstrap();
  await ensureSessionFreshThrottled();
  const a = DefaultStore.authStore;
  if (!a.userId) throw redirect(signinLocationPreservingReturnPath(request));
  if (!hasRecognizedAppRole(a)) {
    await handleMissingAppRole();
  }
  return null;
}

/**
 * Public calculator-like view loader for shared links.
 * Intentionally does not require authentication.
 */
export async function deallensViewLoader() {
  scheduleBootstrapInBackground();
  return null;
}

/**
 * Public auth pages (`/signup`, `/confirm-signup`) should not be visible to authenticated users.
 * Redirects signed-in users to their role default path.
 */
export async function publicAuthOnlyLoader() {
  await bootstrap();
  const a = DefaultStore.authStore;
  if (!a.userId) return null;
  if (!hasRecognizedAppRole(a)) {
    await handleMissingAppRole();
  }
  throw redirect(getDefaultRoute(a));
}

/**
 * HAI-757 public `/signup` request-access page — dev-only until prod-ready.
 * Does not affect DealLens share-link registration or `/confirm-signup`.
 */
export async function devOnlySignupLoader() {
  if (!IS_DEV) {
    throw redirect("/signin");
  }
  return publicAuthOnlyLoader();
}

/**
 * Factory: loader that redirects to / if allow( authStore ) is false.
 * SECURITY: Per-route permission check; redirect prevents rendering protected content.
 */
export function createAllowLoader(allow, options = {}) {
  const requiredRole = String(options?.requiredRole ?? "")
    .trim()
    .toLowerCase();
  return async function allowLoader() {
    await bootstrap();
    const a = DefaultStore.authStore;
    // Loaders run in parallel with protectedLayoutLoader; unauth must not redirect to getDefaultRoute (sign-in owns that).
    if (!a.userId) return null;
    if (!hasRecognizedAppRole(a)) return null;
    if (!allow(a)) {
      if (requiredRole && a?.hasRole?.(requiredRole)) {
        a?.setActiveRole?.(requiredRole);
        return null;
      }
      throw redirect(getDefaultRoute(a));
    }
    return null;
  };
}
